import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, FileText, Loader2, Save, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { getOptions, saveDecision, uploadPublished, getDecision } from '@generated/server/decisions';
import { completeness, type Decision } from '@generated/lib/decision-utils';
import { useSession } from './session';
import { MultiSelect } from './MultiSelect';
import { SummaryPreviewModal } from './SummaryPreviewModal';

type Field = { key: keyof Decision; label: string; type: 'text' | 'textarea' | 'select' | 'multi' | 'date'; opt?: string; only?: 'itp' | 'sd'; perm?: boolean };
const GROUPS: { title: string; fields: Field[] }[] = [
  { title: 'Decision Overview', fields: [
    { key: 'document', label: 'Document', type: 'select', opt: 'document' }, { key: 'date', label: 'Date of Decision', type: 'date' },
    { key: 'summary', label: 'Decision Summary', type: 'textarea' }, { key: 'keywords', label: 'Keywords', type: 'multi', opt: 'keywords' } ] },
  { title: 'Adjudication', fields: [
    { key: 'adjudicator', label: 'Adjudicator', type: 'text' }, { key: 'sport', label: 'Sport', type: 'select', opt: 'sport' },
    { key: 'process', label: 'Process', type: 'select', opt: 'process', only: 'sd' }, { key: 'level', label: 'Level', type: 'select', opt: 'level' },
    { key: 'province', label: 'Province', type: 'select', opt: 'province' }, { key: 'jurisdiction', label: 'Jurisdiction', type: 'multi', opt: 'jurisdiction' },
    { key: 'complaintType', label: 'Complaint Type', type: 'multi', opt: 'complaintType' }, { key: 'finding', label: 'Finding', type: 'multi', opt: 'finding' },
    { key: 'anonymous', label: 'Anonymous Complainant', type: 'select', opt: 'anonymousComplainant' } ] },
  { title: 'Respondent', fields: [
    { key: 'respondentName', label: "Respondent's Name", type: 'text', perm: true }, { key: 'respondentAge', label: "Respondent's Age", type: 'select', opt: 'age' },
    { key: 'respondentRole', label: "Respondent's Role", type: 'multi', opt: 'respondentsRole', only: 'itp' }, { key: 'respondentGender', label: "Respondent's Gender", type: 'multi', opt: 'respondentsGender', only: 'itp' } ] },
  { title: 'Outcome & Sanctions', fields: [
    { key: 'sanctions', label: 'Sanctions', type: 'multi', opt: 'sanctions' }, { key: 'suspensionDuration', label: 'Suspension Duration', type: 'text' },
    { key: 'severityWarning', label: 'Severity Warning / Long Term Sanction', type: 'select', opt: 'severityWarningLongTermSanction', only: 'itp' },
    { key: 'educationalRequirement', label: 'Educational Requirement', type: 'text' }, { key: 'mitigating', label: 'Mitigating Factors', type: 'multi', opt: 'mitigatingFactors' },
    { key: 'aggravating', label: 'Aggravating Factors', type: 'multi', opt: 'aggravatingFactors' }, { key: 'substantiated', label: 'Substantiated Issues', type: 'multi', opt: 'substantiatedIssues', only: 'itp' },
    { key: 'unsubstantiated', label: 'Unsubstantiated Issues', type: 'multi', opt: 'unsubstantiatedIssues', only: 'itp' } ] },
  { title: 'Internal Notes', fields: [{ key: 'notes', label: 'Notes', type: 'textarea' }] },
];

export function ReviewDecisionModal({ decision, onClose, onSaved, allowPublish = true }: { decision: Decision; onClose: () => void; onSaved: (d: Decision) => void; allowPublish?: boolean }) {
  const { sid, user } = useSession();
  const [form, setForm] = useState<Decision>(decision);
  const [opts, setOpts] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [published, setPublished] = useState(decision.published);
  const [summary, setSummary] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const canNames = !!user?.permissions.includes('respondent_names');

  useEffect(() => {
    getOptions({ data: { sid, lib: decision.lib } }).then((r) => {
      const o = r?.options ?? {};
      setOpts({ ...o, age: decision.lib === 'itp' ? o.respondentsAge ?? [] : o.respondentsAgeBracket ?? [] });
    }).catch((e) => console.error(e));
  }, [sid, decision.lib]);

  const set = (k: keyof Decision, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    const patch: Record<string, unknown> = {};
    GROUPS.flatMap((g) => g.fields).forEach((f) => {
      if (JSON.stringify(form[f.key]) !== JSON.stringify(decision[f.key])) patch[f.key] = form[f.key];
    });
    if (!Object.keys(patch).length) { toast('No changes to save'); return; }
    setSaving(true);
    try {
      const r = await saveDecision({ data: { sid, lib: decision.lib, id: decision.id, patch } });
      if (r?.decision) onSaved(r.decision);
      toast.success('Decision saved');
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not save the decision.');
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!file) return;
    if (completeness(form) < 5 && !window.confirm('Some key fields (finding, complaint type, date, summary, sanctions) are empty. Publish anyway?')) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('sid', sid); fd.append('lib', decision.lib); fd.append('id', decision.id); fd.append('file', file);
      await uploadPublished({ data: fd });
      setPublished(true);
      setFile(null);
      toast.success('Decision published to the library');
      const r = await getDecision({ data: { sid, lib: decision.lib, id: decision.id } });
      if (r?.decision) onSaved(r.decision);
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : '';
      toast.error(/UPLOAD_NOT_SUPPORTED/.test(msg) ? 'Uploads are not supported here — attach the PDF on the monday board instead.' : msg || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  const fieldEl = (f: Field) => {
    const v = form[f.key];
    if (f.type === 'textarea') return <textarea value={String(v ?? '')} onChange={(e) => set(f.key, e.target.value)} rows={4} className="glass-input w-full rounded-xl p-3 text-sm outline-none" />;
    if (f.type === 'multi') return <MultiSelect value={(v as string[]) ?? []} options={opts[f.opt ?? ''] ?? []} onChange={(x) => set(f.key, x)} />;
    if (f.type === 'select') return (
      <select value={String(v ?? '')} onChange={(e) => set(f.key, e.target.value)} className="glass-input h-10 w-full rounded-xl px-2 text-sm outline-none">
        <option value="">—</option>
        {[...new Set([...(opts[f.opt ?? ''] ?? []), ...(v ? [String(v)] : [])])].map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    if (f.type === 'date') return <input type="date" value={v ? String(v).slice(0, 10) : ''} onChange={(e) => set(f.key, e.target.value ? new Date(e.target.value).toISOString() : null)} className="glass-input h-10 w-full rounded-xl px-3 text-sm outline-none" />;
    return <input value={String(v ?? '')} onChange={(e) => set(f.key, e.target.value)} className="glass-input h-10 w-full rounded-xl px-3 text-sm outline-none" />;
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          <div className="border-b border-border px-6 py-4">
            <DialogTitle className="text-xl font-semibold text-secondary">Review Decision</DialogTitle>
            <p className="text-sm text-muted-foreground">{decision.name}</p>
          </div>
          <div className="max-h-[70vh] flex-1 overflow-y-auto px-6 py-5">
            {GROUPS.map((g) => {
              const fields = g.fields.filter((f) => (!f.only || f.only === decision.lib) && (!f.perm || canNames));
              return (
                <section key={g.title} className="mb-6">
                  <h2 className="mb-3 border-b border-border pb-1.5 text-xs font-semibold uppercase tracking-wider text-secondary">{g.title}</h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {fields.map((f) => (
                      <label key={f.key} className={`block min-w-0 ${f.type === 'textarea' ? 'sm:col-span-2' : ''}`}>
                        <span className="mb-1 block text-xs font-medium text-muted-foreground">{f.label}</span>
                        {fieldEl(f)}
                      </label>
                    ))}
                  </div>
                </section>
              );
            })}
            {allowPublish && (
              <section>
                <h2 className="mb-3 border-b border-border pb-1.5 text-xs font-semibold uppercase tracking-wider text-secondary">Published Decision</h2>
                {published ? (
                  <p className="flex items-center gap-2 rounded-xl bg-[hsl(var(--success-soft))] px-4 py-3 text-sm font-medium text-[hsl(var(--success))]"><CheckCircle2 className="h-4 w-4" />Published — visible in the library</p>
                ) : (
                  <div>
                    <button type="button" onClick={() => inputRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }} className="flex w-full flex-col items-center rounded-xl border-2 border-dashed border-secondary/20 px-4 py-8 text-center hover:border-primary/40">
                      <UploadCloud className="h-8 w-8 text-secondary/40" />
                      <p className="mt-2 text-sm font-medium text-secondary">Drop the final decision PDF here</p>
                      <p className="text-xs text-muted-foreground">or click to browse — PDF</p>
                      {file && <p className="mt-2 text-xs font-medium text-primary">{file.name} · {Math.round(file.size / 1024)} KB</p>}
                    </button>
                    <input ref={inputRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    <button onClick={publish} disabled={!file || uploading} className="mt-3 flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                      {uploading ? <><Loader2 className="h-4 w-4 animate-spin" />Uploading…</> : <><UploadCloud className="h-4 w-4" />Upload Published Decision</>}
                    </button>
                  </div>
                )}
              </section>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-border bg-muted/70 px-6 py-3">
            <button onClick={onClose} className="h-10 rounded-xl px-4 text-sm text-muted-foreground hover:text-secondary">Cancel</button>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setSummary(true)} className="flex h-10 items-center gap-1.5 rounded-xl px-3 text-sm font-medium text-secondary hover:bg-card"><FileText className="h-4 w-4" />Summary</button>
              <button onClick={save} disabled={saving} className="flex h-10 items-center gap-1.5 rounded-xl border border-primary/30 bg-card px-4 text-sm font-semibold text-primary disabled:opacity-60">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : <><Save className="h-4 w-4" />Save Changes</>}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {summary && <SummaryPreviewModal lib={decision.lib} id={decision.id} onClose={() => setSummary(false)} />}
    </>
  );
}
